/**
 * useSincronizacaoDoPush — os três gatilhos da sincronização do push (plano "Push: religar sozinho ou avisar", Decisão 6):
 *
 *   - `abertura`: na montagem (o `ReinscricaoDoPush` monta no shell já autenticado);
 *   - `foco`: quando a aba volta a ficar visível (`visibilitychange`). O app instalado quase nunca reinicia, e este é o gatilho que vale
 *     para ele;
 *   - `mensagem-do-sw`: quando o service worker avisa que a inscrição girou (`pushsubscriptionchange`, `lib/swPush.ts`). As outras
 *     mensagens do SW (`chamada:abrir`, `chamada:ir`) são de outros ouvintes e passam batido.
 *
 * Quem decide se vale a pena perguntar de novo (intervalos mínimos, uma execução por vez, lock entre abas) é o `sincronizarPush`;
 * aqui só se dispara. O evento `seweb:push-mudou`, que o `sincronizarPush` emite ao terminar, NÃO é ouvido aqui: ouvi-lo faria cada
 * sincronização disparar a próxima (Review Focus 4).
 *
 * Devolve o último resultado que não foi `pulada` (`null` antes do primeiro): `pulada` quer dizer "nada foi feito agora", então não
 * apaga o que a execução anterior concluiu (o aviso continua na tela até a causa sumir). A Task 8 renderiza o aviso a partir dele.
 *
 * Os ouvintes se registram uma vez por `basePath` (não por render). Ao desmontar, saem, e o `aindaVale` das execuções que ficaram em voo
 * passa a dizer `false`: um "Sair" no meio não pode deixar dono, intenção nem confirmação gravados sem sessão por trás.
 */
import { useEffect, useState } from "react";

import {
  sincronizarPush,
  type MotivoDaSincronizacao,
  type ResultadoDaSincronizacao,
} from "@/lib/push/sincronizarPush";
import { TIPO_DA_MENSAGEM_DE_INSCRICAO_MUDOU } from "@/lib/swPush";

/** A mensagem do service worker é a de "a inscrição girou"? (`data` pode ser `null`, texto ou qualquer coisa.) */
function ehAvisoDeInscricaoMudou(dado: unknown): boolean {
  return (
    typeof dado === "object" &&
    dado !== null &&
    (dado as { tipo?: unknown }).tipo === TIPO_DA_MENSAGEM_DE_INSCRICAO_MUDOU
  );
}

export function useSincronizacaoDoPush(basePath: string): ResultadoDaSincronizacao | null {
  const [resultado, setResultado] = useState<ResultadoDaSincronizacao | null>(null);

  useEffect(() => {
    // O resultado de outro `basePath` não é deste (e, na montagem, `null` por cima de `null` não renderiza de novo).
    setResultado(null);
    let vivo = true;
    const aindaVale = () => vivo;

    const sincronizar = (motivo: MotivoDaSincronizacao) => {
      sincronizarPush(basePath, motivo, { aindaVale })
        .then((novo) => {
          if (vivo && novo.tipo !== "pulada") setResultado(novo);
        })
        // O `sincronizarPush` nunca rejeita; se um dia rejeitar, o gatilho não vira rejeição solta.
        .catch(() => undefined);
    };

    const aoMudarAVisibilidade = () => {
      if (document.visibilityState === "visible") sincronizar("foco");
    };
    const aoReceberMensagem = (evento: MessageEvent) => {
      if (ehAvisoDeInscricaoMudou(evento.data)) sincronizar("mensagem-do-sw");
    };

    document.addEventListener("visibilitychange", aoMudarAVisibilidade);
    const servico = typeof navigator === "undefined" ? undefined : navigator.serviceWorker;
    servico?.addEventListener("message", aoReceberMensagem);
    sincronizar("abertura");

    return () => {
      vivo = false;
      document.removeEventListener("visibilitychange", aoMudarAVisibilidade);
      servico?.removeEventListener("message", aoReceberMensagem);
    };
  }, [basePath]);

  return resultado;
}
